import React from "react";
import {
  YStack,
  XStack,
  Image,
  SizableText,
  H3,
  H4,
  Card,
  Separator,
  Button,
  Spinner
} from "tamagui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { MaterialCommunityIcons, MaterialIcons } from "@expo/vector-icons";
import * as Animatable from 'react-native-animatable';
import { View, Linking, Dimensions, Platform } from "react-native";
import ErrorBoundary from "@utils/ErrorBoundary";
import { themeSelector } from "@reduxLocal/selectors/themeSelector";
import apis from "@api/api";
import { useAuthRequest, ResponseType } from 'expo-auth-session';
import { createURL } from 'expo-linking';
import { connectPlatformCallback } from "@api/api";
import { useDispatch } from "react-redux";
import { ADD_INTEGRATION, HIDE_LOADING_MODAL, SHOW_AUTH_MODAL, SHOW_LOADING_MODAL } from "@reduxLocal/actions/types";

interface XOptionsProps {
  integration?: {
    meta: string;
    title: string;
    endPoint?: string;
    clientId?: string;
    clientSecret?: string;
    clientKey?: string;
    scopes?: string[];
  };
  onReconnect?: () => void;
}

const XOptions = ({ integration, onReconnect }: XOptionsProps) => {
  const theme = themeSelector();
  const screenWidth = Dimensions.get('window').width;
  const isTablet = screenWidth > 768;
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  // Setup OAuth for reconnection if integration data is provided
  const discovery = integration?.endPoint ? { authorizationEndpoint: integration.endPoint } : null;
  const [request, response, promptAsync] = useAuthRequest(
    integration?.endPoint ? {
      clientId: integration.clientId || '',
      scopes: integration.scopes || [],
      redirectUri: Platform.select({ web: createURL('/universal/integrations'), default: `https://api.smartsaas.pro/app-redirect` }),
      responseType: ResponseType.Code,
      extraParams: {
        client_key: integration.clientKey || integration.clientId || '',
      }
    } : null,
    discovery || null
  );

  const handleGotCode = async (code: string, verifier?: string) => {
    if (!integration) return;
    try {
      dispatch({ type: SHOW_LOADING_MODAL });
      const res = await connectPlatformCallback(code, integration.meta, request?.codeVerifier || '', verifier);
      dispatch({ type: ADD_INTEGRATION, payload: { authType: integration.meta, data: res.data } });
      dispatch({ type: HIDE_LOADING_MODAL });
      dispatch({ type: SHOW_AUTH_MODAL, data: { content: res.message, type: 'success' } });

      // Refresh integration data and X profile
      queryClient.invalidateQueries({ queryKey: ['integrationData'] });
      queryClient.invalidateQueries({ queryKey: ['xProfile'] });
      queryClient.refetchQueries({ queryKey: ['xProfile'] });
    } catch (error: any) {
      console.error('X reconnection error:', error);
      dispatch({ type: HIDE_LOADING_MODAL });
      dispatch({ type: SHOW_AUTH_MODAL, data: { content: `Failed to reconnect ${integration.title}: ${error.message}`, type: 'error' } });
    }
  };

  React.useEffect(() => {
    if (response?.type === 'success' && integration) {
      const { code } = response.params;
      // Pass redirectUri to ensure it matches what was used in authorization request
      handleGotCode(code, request?.redirectUri);
    }
  }, [response]);

  const handleReconnect = async () => {
    if (onReconnect) {
      onReconnect();
    } else if (integration?.endPoint && promptAsync) {
      try {
        await promptAsync({ windowFeatures: { dependant: true, name: 'auth' } });
      } catch (e: any) {
        dispatch({ type: SHOW_AUTH_MODAL, data: { content: `Failed to start reconnection: ${e}`, type: "error" } });
      }
    } else {
      // Fallback: just retry the query
      refetch();
    }
  };

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["xProfile"],
    queryFn: async () => await apis.getXProfile(),
    staleTime: 1000 * 60 * 5,
    retry: 2,
  });

  return (
    <ErrorBoundary>
      <YStack gap="$4">
        {/* Error State */}
        {error && (
          <Animatable.View animation="fadeInUp" duration={600}>
            <Card
              backgroundColor={theme ? "#fef2f2" : "#450a0a"}
              borderColor={theme ? "#fecaca" : "#7f1d1d"}
              borderRadius={16}
              padding="$5"
              elevate
            >
              <YStack alignItems="center" gap="$4">
                <View style={{
                  backgroundColor: theme ? "#fee2e2" : "#7f1d1d",
                  borderRadius: 50,
                  padding: 16
                }}>
                  <MaterialIcons name="error-outline" size={48} color="#ef4444" />
                </View>
                <H3 color="#ef4444" fontWeight="600" textAlign="center">
                  Failed to load X profile
                </H3>
                <SizableText
                  size="$4"
                  color={theme ? "#64748b" : "#94a3b8"}
                  textAlign="center"
                >
                  {error?.response?.status === 401 || error?.response?.status === 403
                    ? "Your X (Twitter) connection has expired. Please reconnect your account."
                    : "Please try reconnecting your X (Twitter) account, or try removing and re-adding the integration."}
                </SizableText>
                <YStack gap="$3" width="100%">
                  <Button
                    backgroundColor="#1DA1F2"
                    color="white"
                    onPress={handleReconnect}
                    borderRadius={12}
                    paddingHorizontal="$5"
                    paddingVertical="$3"
                  >
                    <XStack alignItems="center" gap="$2">
                      <MaterialIcons name="link" size={20} color="white" />
                      <SizableText color="white" fontWeight="600">
                        Reconnect X Account
                      </SizableText>
                    </XStack>
                  </Button>
                  <Button
                    backgroundColor={theme ? "#f3f4f6" : "#374151"}
                    color={theme ? "#1e293b" : "#f1f5f9"}
                    onPress={() => refetch()}
                    borderRadius={12}
                    paddingHorizontal="$5"
                    paddingVertical="$3"
                  >
                    <XStack alignItems="center" gap="$2">
                      <MaterialIcons name="refresh" size={20} color={theme ? "#1e293b" : "#f1f5f9"} />
                      <SizableText color={theme ? "#1e293b" : "#f1f5f9"} fontWeight="600">
                        Retry
                      </SizableText>
                    </XStack>
                  </Button>
                </YStack>
              </YStack>
            </Card>
          </Animatable.View>
        )}

        {/* Loading State */}
        {isLoading && (
          <Animatable.View animation="fadeIn" duration={600}>
            <Card
              backgroundColor={theme ? "#f8fafc" : "#1e293b"}
              borderColor={theme ? "#e2e8f0" : "#334155"}
              borderRadius={16}
              padding="$6"
              elevate
            >
              <YStack alignItems="center" gap="$4">
                <Spinner size="large" color="#1DA1F2" />
                <H4 color={theme ? "#1e293b" : "#f1f5f9"} fontWeight="600">
                  Loading X profile...
                </H4>
              </YStack>
            </Card>
          </Animatable.View>
        )}

        {/* Profile Data */}
        {data?.profile && (
          <>
            <Animatable.View animation="fadeInUp" duration={600} delay={100}>
              <Card
                backgroundColor={theme ? "#ffffff" : "#1e293b"}
                borderColor={theme ? "#e2e8f0" : "#334155"}
                borderRadius={16}
                padding="$5"
                elevate
              >
                {/* Header with Profile Info */}
                <XStack alignItems="center" gap="$4" marginBottom="$4">
                  <View style={{
                    width: 70,
                    height: 70,
                    borderRadius: 35,
                    overflow: 'hidden'
                  }}>
                    <Image
                      source={{ uri: data.profile.profile_image_url?.replace("_normal", "_bigger") }}
                      width={70}
                      height={70}
                    />
                  </View>

                  <YStack flex={1}>
                    <XStack alignItems="center" gap="$2">
                      <H4
                        color={theme ? "#1e293b" : "#f1f5f9"}
                        fontWeight="700"
                        numberOfLines={1}
                      >
                        {data.profile.name}
                      </H4>
                      {data.profile.verified && (
                        <MaterialCommunityIcons
                          name="check-decagram"
                          size={20}
                          color="#1DA1F2"
                        />
                      )}
                    </XStack>
                    <SizableText
                      size="$4"
                      color={theme ? "#64748b" : "#94a3b8"}
                      numberOfLines={1}
                    >
                      @{data.profile.username}
                    </SizableText>
                  </YStack>
                </XStack>

                <Separator borderColor={theme ? "#e2e8f0" : "#334155"} marginBottom="$4" />

                {/* Statistics */}
                <YStack gap="$3">
                  <XStack gap="$3" justifyContent="space-between" flexWrap="wrap">
                    <Stat
                      icon="account-group"
                      label="Followers"
                      value={data.profile.public_metrics?.followers_count || 0}
                      theme={theme}
                    />
                    <Stat
                      icon="account-plus"
                      label="Following"
                      value={data.profile.public_metrics?.following_count || 0}
                      theme={theme}
                    />
                  </XStack>
                  <XStack gap="$3" justifyContent="space-between" flexWrap="wrap">
                    <Stat
                      icon="file-text"
                      label="Tweets"
                      value={data.profile.public_metrics?.tweet_count || 0}
                      theme={theme}
                    />
                    <Stat
                      icon="heart"
                      label="Likes"
                      value={data.profile.public_metrics?.like_count || 0}
                      theme={theme}
                    />
                  </XStack>
                </YStack>

                {/* Action Button */}
                <Button
                  backgroundColor="#1DA1F2"
                  color="white"
                  marginTop="$4"
                  onPress={async () => {
                    const url = `https://twitter.com/${data.profile.username}`;
                    try {
                      await Linking.openURL(url);
                    } catch (error) {
                      console.error("Error opening X profile:", error);
                    }
                  }}
                  borderRadius={12}
                  paddingVertical="$3"
                >
                  <XStack alignItems="center" gap="$2">
                    <MaterialIcons name="open-in-new" size={18} color="white" />
                    <SizableText color="white" fontWeight="600" size="$4">
                      View Profile on X
                    </SizableText>
                  </XStack>
                </Button>
              </Card>
            </Animatable.View>

            {/* Success Message */}
            <Animatable.View animation="fadeInUp" duration={600} delay={200}>
              <Card
                backgroundColor={theme ? "#f0fdf4" : "#064e3b"}
                borderColor={theme ? "#dcfce7" : "#065f46"}
                borderRadius={16}
                padding="$5"
                elevate
              >
                <YStack alignItems="center" gap="$3">
                  <View style={{
                    backgroundColor: theme ? "#d1fae5" : "#065f46",
                    borderRadius: 50,
                    padding: 12
                  }}>
                    <MaterialIcons name="check-circle" size={32} color={theme ? "#10b981" : "#34d399"} />
                  </View>
                  <H4
                    color={theme ? "#059669" : "#10b981"}
                    fontWeight="600"
                    textAlign="center"
                  >
                    X Connected Successfully!
                  </H4>
                  <SizableText
                    size="$4"
                    color={theme ? "#065f46" : "#34d399"}
                    textAlign="center"
                  >
                    You can now access your X (Twitter) profile and tweets directly from SmartSaaS.
                  </SizableText>
                </YStack>
              </Card>
            </Animatable.View>
          </>
        )}
      </YStack>
    </ErrorBoundary>
  );
};

const Stat = ({ icon, label, value, theme }: { icon: string; label: string; value: number; theme: boolean }) => (
  <YStack alignItems="center" gap="$2" flex={1} minWidth={100}>
    <View style={{
      backgroundColor: theme ? "#dbeafe" : "#1e3a8a",
      borderRadius: 12,
      padding: 10
    }}>
      <MaterialCommunityIcons name={icon as any} size={24} color="#1DA1F2" />
    </View>
    <SizableText
      fontSize="$6"
      fontWeight="700"
      color={theme ? "#1e293b" : "#f1f5f9"}
    >
      {value.toLocaleString()}
    </SizableText>
    <SizableText
      fontSize="$3"
      color={theme ? "#64748b" : "#94a3b8"}
      textAlign="center"
    >
      {label}
    </SizableText>
  </YStack>
);

export default XOptions;
